/**
 * The permission catalogue — the authoritative list of everything the system can
 * authorise. Both sides depend on it: the API seeds the `Permission` table from
 * it and guards routes with it, the web app hides UI with it.
 *
 * A permission key is always `<module>:<action>`. Adding a module here and
 * re-running the seed is the only supported way to introduce a new permission.
 */

export const ACTIONS = [
  'view',
  'create',
  'update',
  'delete',
  'export',
  'import',
  'approve',
  'assign',
  'convert',
  'cancel',
  // Moving a record along a defined workflow, as distinct from editing its fields.
  // A site engineer may record installation progress without being allowed to
  // declare the project commissioned.
  'advance',
  'upload',
  'verify',
  'dispatch',
  'receive',
  // Issuing an approved document outward to a counterparty. Approving a
  // purchase order and putting it in the supplier's hands commit the company to
  // different things, so they are different signatures.
  'send',
  // Committing a document's effect to the ledger. Recording a delivery and
  // crediting stock against it are held apart for the same reason a stock
  // adjustment separates entry from approval.
  'post',
  'settle',
  // Pushing a record's findings outward into another part of the system, as
  // distinct from editing it. A surveyor fills in a survey; forwarding it can
  // create a customer record, and minting customers is not a field authority.
  'sync',
  'manage',
] as const;

export type ActionKey = (typeof ACTIONS)[number];

const CRUD = ['view', 'create', 'update', 'delete'] as const;

export const MODULE_PERMISSIONS = {
  dashboard: ['view', 'export'],
  crm: ['view', 'create', 'update', 'delete', 'assign', 'export'],
  leads: [...CRUD, 'assign', 'convert', 'import', 'export'],
  customers: [...CRUD, 'import', 'export'],
  site_surveys: [...CRUD, 'assign', 'approve', 'sync', 'export'],
  quotations: [...CRUD, 'approve', 'convert', 'cancel', 'export'],
  work_orders: [...CRUD, 'approve', 'cancel', 'export'],
  projects: [...CRUD, 'assign', 'advance', 'cancel', 'export'],
  system_designs: [...CRUD, 'approve', 'upload', 'export'],
  customer_approvals: [...CRUD, 'approve', 'upload', 'export'],
  material_planning: [...CRUD, 'approve', 'export'],
  fabrication: [...CRUD, 'assign', 'approve', 'export'],
  electrical_testing: [...CRUD, 'approve', 'export'],
  net_metering: [...CRUD, 'approve', 'export'],
  project_closure: [...CRUD, 'approve', 'export'],
  warranties: [...CRUD, 'export'],
  // After-sales. `advance` moves a ticket along its own workflow and is held
  // apart from `update` for the same reason it is on projects: a call handler
  // may correct a description without being able to declare a fault resolved.
  // `upload` covers the site photographs and the signed service report — the
  // evidence a warranty claim rests on, which is why attaching it is a distinct
  // authority from editing the ticket text around it.
  service: [...CRUD, 'assign', 'advance', 'cancel', 'upload', 'export'],
  // The after-sales overview. Its own key rather than an action on `service`
  // because it reads across tickets, visits, warranties and AMCs, and a
  // dispatcher who works one queue does not necessarily get the whole picture.
  service_dashboard: ['view', 'export'],
  amc: [...CRUD, 'approve', 'cancel', 'export'],
  discom: [...CRUD, 'upload', 'export'],
  installation: [...CRUD, 'assign', 'approve', 'export'],
  commissioning: [...CRUD, 'approve', 'export'],
  subsidy: [...CRUD, 'upload', 'verify', 'export'],
  payment_followups: [...CRUD, 'assign', 'export'],
  referrals: [...CRUD, 'approve', 'settle', 'export'],
  // Purchase authority splits the same way stock movement does: raising a
  // request, committing the company to a price, and accepting what turned up on
  // the loading bay are three different signatures.
  vendors: [...CRUD, 'export'],
  purchase_requisitions: [...CRUD, 'approve', 'convert', 'cancel', 'export'],
  // `send` is separate from `approve` because internal sign-off and issuing the
  // order to the supplier commit the company to different things.
  purchase_orders: [...CRUD, 'approve', 'send', 'cancel', 'export'],
  // `post` is what moves stock, and is held apart from `receive` so that
  // recording a delivery and crediting the ledger can sit with different people.
  goods_receipt: [...CRUD, 'receive', 'approve', 'post', 'cancel', 'export'],
  purchase_returns: [...CRUD, 'approve', 'cancel', 'export'],
  ftr_uploads: ['view', 'create', 'delete', 'upload', 'verify', 'export'],
  inventory: [...CRUD, 'import', 'export'],
  // Stock movement is split from `inventory` because the authority differs at
  // each step. A storekeeper reads balances and picks against an allocation; a
  // manager approves a transfer; writing stock off against a physical count is
  // its own signature, and holding it apart is what makes the audit trail mean
  // something.
  stock_allocations: [...CRUD, 'cancel', 'export'],
  stock_transfers: [...CRUD, 'approve', 'dispatch', 'receive', 'cancel', 'export'],
  stock_adjustments: [...CRUD, 'approve', 'cancel', 'export'],
  dealers: [...CRUD, 'approve', 'export'],
  dealer_pricing: [...CRUD, 'approve', 'import', 'export'],
  proforma_invoices: [...CRUD, 'approve', 'convert', 'cancel', 'export'],
  dispatch: [...CRUD, 'dispatch', 'cancel', 'export'],
  serials: ['view', 'create', 'update', 'delete', 'import', 'export'],
  dcr_compliance: ['view', 'create', 'update', 'verify', 'export'],
  tenders: [...CRUD, 'approve', 'export'],
  expenses: [...CRUD, 'approve', 'settle', 'export'],
  payments: [...CRUD, 'approve', 'cancel', 'export'],
  // The finance overview. View-only by design: every figure on it is derived
  // from documents that carry their own permissions, so there is nothing here
  // to grant a right over.
  finance: ['view', 'export'],
  // `post` is the issue: it spends an invoice number, freezes the document and
  // creates the receivable. Distinct from `create` because raising the draft
  // and committing it to the GST return are not the same signature.
  sales_invoices: [...CRUD, 'post', 'send', 'cancel', 'export'],
  receivables: ['view', 'export'],
  payables: ['view', 'export'],
  // `update` retries or skips a single stuck job; `manage` reconfigures what
  // syncs at all and triggers a full run. A collections clerk clearing a failed
  // invoice push should not thereby be able to repoint the integration.
  integrations: ['view', 'update', 'manage', 'export'],
  // ---- HR & employee management ------------------------------------
  // The overview. View-only for the same reason `finance` is: every figure on
  // it is derived from records that carry their own permissions.
  hr: ['view', 'export'],
  hr_employees: [...CRUD, 'import', 'export'],
  // `approve` is regularising somebody else's day — correcting a missed punch
  // or converting an absence into leave. Marking your own attendance is
  // `create`, and the two are deliberately not the same signature.
  hr_attendance: [...CRUD, 'approve', 'import', 'export'],
  // `manage` configures leave types and opening balances; `approve` decides one
  // request. A manager who can grant leave should not also be able to invent a
  // leave type with a forty-day quota and then grant himself that.
  hr_leaves: [...CRUD, 'approve', 'cancel', 'manage', 'export'],
  // Salary is the most sensitive data the system holds, so it is its own module
  // rather than an action on `hr_employees` — nobody sees pay by virtue of
  // being allowed to see people.
  hr_payroll: [...CRUD, 'approve', 'export'],
  hr_tasks: [...CRUD, 'assign', 'export'],
  // `approve` finalises a review, which is what freezes its figures.
  hr_performance: [...CRUD, 'approve', 'export'],

  reports: ['view', 'export'],
  // Outbound customer communication and the record of it.
  //
  // `send` is separate from `view` for the reason it is separate on a purchase
  // order: reading what was said to a customer and saying something to them
  // under the company's name are different acts, and the second one costs money
  // per message and cannot be taken back. `manage` configures the templates —
  // which message goes down which channel, and what the tenant's approved
  // template is called at Meta — and is an administrator's authority, not a
  // salesperson's.
  //
  // Deliberately not folded into `integrations`. That key belongs to people who
  // watch a sync queue; the communication log is customer data and belongs to
  // the people who deal with customers. A feature must not launder access to
  // the module whose data it touches — the same line the reports build drew for
  // dashboards.
  communications: ['view', 'send', 'manage', 'export'],
  notifications: ['view', 'create', 'manage'],
  users: [...CRUD, 'assign', 'manage', 'export'],
  roles: [...CRUD, 'manage'],
  company_settings: ['view', 'update', 'manage'],
  master_data: [...CRUD, 'import', 'export'],
  audit_logs: ['view', 'export'],
} as const satisfies Record<string, readonly ActionKey[]>;

export type ModuleKey = keyof typeof MODULE_PERMISSIONS;

/** `'leads:create' | 'leads:view' | ...` — every legal permission string. */
export type PermissionKey = {
  [M in ModuleKey]: `${M}:${(typeof MODULE_PERMISSIONS)[M][number]}`;
}[ModuleKey];

export const MODULE_KEYS = Object.keys(MODULE_PERMISSIONS) as ModuleKey[];

/** Flat list used by the permission seeder and the role editor. */
export const ALL_PERMISSIONS: PermissionKey[] = MODULE_KEYS.flatMap((moduleKey) =>
  MODULE_PERMISSIONS[moduleKey].map((action) => `${moduleKey}:${action}` as PermissionKey),
);

export const permissionKey = <M extends ModuleKey>(
  moduleKey: M,
  action: (typeof MODULE_PERMISSIONS)[M][number],
): PermissionKey => `${moduleKey}:${action}` as PermissionKey;

export const isPermissionKey = (value: string): value is PermissionKey =>
  (ALL_PERMISSIONS as string[]).includes(value);

/* ------------------------------------------------------------------ */
/* System roles                                                        */
/* ------------------------------------------------------------------ */

/**
 * Seeded roles. `SUPER_ADMIN` is special-cased in the authorisation layer and
 * bypasses permission checks entirely; the rest are ordinary permission sets and
 * are fully editable once seeded.
 *
 * The functional specification names ten roles. Five already existed here under
 * a longer name and are *not* duplicated — a second role granting the same
 * things is how two roles drift apart and nobody can say which one is real:
 *
 *   specification      seeded slug            note
 *   ─────────────────  ─────────────────────  ─────────────────────────────────
 *   Management         management             added
 *   Sales              sales_executive        `sales_manager` supervises
 *   Telecalling        telecalling            added
 *   Survey Engineer    survey_engineer        added
 *   Installation Team  installation_team      added
 *   Warehouse          store_keeper           existing
 *   Purchase           procurement_officer    existing
 *   Accounts           accounts               existing
 *   HR                 hr_manager             existing
 *   Service Team       service_team           added
 *
 * `management` is deliberately not `admin`. The specification gives Management
 * dashboards, reports, approvals and all *business* data; it does not give them
 * user administration, role editing or company settings. Those stay with the
 * administrator, so that granting somebody sight of the whole business does not
 * also hand them the ability to rewrite who may see it.
 */
export const SYSTEM_ROLES = [
  'super_admin',
  'admin',
  'management',
  'sales_manager',
  'sales_executive',
  'telecalling',
  'project_manager',
  'site_engineer',
  'survey_engineer',
  'installation_team',
  'procurement_officer',
  'store_keeper',
  'accounts',
  'hr_manager',
  'service_team',
  'dealer',
  'viewer',
] as const;

export type SystemRoleSlug = (typeof SYSTEM_ROLES)[number];
